import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from './MainLayout';
import { 
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { SortablePaginatedTable } from './common';
import { formatDate, formatCurrency } from '../utils/dateUtils';

// Define column configurations for the tables
const recentInvoicesColumns = [
  { id: 'account', label: 'Account', sortable: true },
  { id: 'business_unit', label: 'Business Unit', sortable: true },
  { id: 'contract_number', label: 'Contract #', sortable: true },
  { id: 'number', label: 'Invoice #', sortable: true },
  { 
    id: 'invoice_date', 
    label: 'Invoice Date', 
    sortable: true,
    render: (row) => formatDate(row.invoice_date)
  },
  { 
    id: 'amount', 
    label: 'Amount', 
    sortable: true,
    align: 'right',
    render: (row) => formatCurrency(row.amount)
  },
  { 
    id: 'points', 
    label: 'Points', 
    sortable: true,
    align: 'right',
    render: (row) => row.points !== null && row.points !== undefined ? parseFloat(row.points).toFixed(2) : 'N/A'
  }
];

const recentPaymentsColumns = [
  { id: 'account', label: 'Account', sortable: true },
  { id: 'business_unit', label: 'Business Unit', sortable: true },
  { 
    id: 'payment_date', 
    label: 'Payment Date', 
    sortable: true,
    render: (row) => formatDate(row.payment_date)
  },
  { 
    id: 'amount', 
    label: 'Amount', 
    sortable: true,
    align: 'right',
    render: (row) => formatCurrency(row.amount)
  }
];

export const TransactionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectionErrors, setSectionErrors] = useState({
    recentInvoices: false,
    recentPayments: false
  });
  const [transactionsData, setTransactionsData] = useState({
    recentInvoices: [],
    recentPayments: []
  });

  // Create a dedicated function for fetching data
  const fetchTransactionsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSectionErrors({
        recentInvoices: false,
        recentPayments: false
      });
      
      // Use the API base URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      console.log('Using API base URL:', apiBaseUrl);
      
      // Fetch all data but handle each section's errors separately
      const newTransactionsData = { ...transactionsData };
      const newSectionErrors = { ...sectionErrors };
      
      try {
        // Fetch recent invoices
        const invoicesResponse = await fetch(`${apiBaseUrl}/api/invoices/recent`).then(res => {
          if (!res.ok) throw new Error(`Failed to fetch recent invoices: ${res.status}`);
          return res.json();
        });
        console.log('Recent invoices data:', invoicesResponse);
        newTransactionsData.recentInvoices = invoicesResponse || [];
      } catch (error) {
        console.error('Recent invoices fetch failed:', error);
        newSectionErrors.recentInvoices = true;
      }
      
      try {
        // Fetch recent payments
        const paymentsResponse = await fetch(`${apiBaseUrl}/api/payments/recent`).then(res => {
          if (!res.ok) throw new Error(`Failed to fetch recent payments: ${res.status}`);
          return res.json();
        });
        console.log('Recent payments data:', paymentsResponse);
        newTransactionsData.recentPayments = paymentsResponse || [];
      } catch (error) {
        console.error('Recent payments fetch failed:', error);
        newSectionErrors.recentPayments = true;
      }
      
      // Update state with whatever data we were able to fetch
      setTransactionsData(newTransactionsData);
      setSectionErrors(newSectionErrors);
      
      // Check if all sections failed
      const allSectionsFailed = Object.values(newSectionErrors).every(value => value === true);
      if (allSectionsFailed) {
        setError('Failed to load all transactions data. Please try again later.');
      }
    } catch (error) {
      console.error('Failed to fetch transactions data:', error);
      setError('Failed to load transactions data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Initial data fetch
  useEffect(() => {
    fetchTransactionsData();
  }, [fetchTransactionsData]);
  
  return (
    <MainLayout title="Transactions">
      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <p className="error-details">
            Check the console for more details or contact your administrator.
          </p>
        </div>
      ) : (
        <div className="scrollable-content">
          <div className="tables-container">
            {/* Recent Invoices Table */}
            <div className="table-section">
              <h2 className="table-title">Recent Invoices</h2>
              {sectionErrors.recentInvoices ? (
                <div className="table-error-container">
                  <p className="table-error">Failed to load recent invoices data</p>
                </div>
              ) : transactionsData.recentInvoices.length === 0 ? (
                <div className="table-empty-container">
                  <p className="table-empty">No recent invoices</p>
                </div>
              ) : (
                <SortablePaginatedTable
                  data={transactionsData.recentInvoices}
                  columns={recentInvoicesColumns}
                  rowsPerPage={10}
                  emptyMessage="No recent invoices"
                  tableContainerProps={{ className: "enhanced-table" }}
                  isContractDetailTable={false}
                />
              )}
            </div>
            
            {/* Recent Payments Table */}
            <div className="table-section">
              <h2 className="table-title">Recent Payments</h2>
              {sectionErrors.recentPayments ? (
                <div className="table-error-container">
                  <p className="table-error">Failed to load recent payments data</p>
                </div>
              ) : transactionsData.recentPayments.length === 0 ? (
                <div className="table-empty-container">
                  <p className="table-empty">No recent payments</p>
                </div>
              ) : (
                <SortablePaginatedTable
                  data={transactionsData.recentPayments}
                  columns={recentPaymentsColumns}
                  rowsPerPage={10}
                  emptyMessage="No recent payments"
                  tableContainerProps={{ className: "enhanced-table" }}
                  isContractDetailTable={false}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}; 